import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Autocomplete, Box, Chip, FilterOptionsState, TextField, Typography } from '@mui/material';

import { MemberListResult } from 'stores/MemberStoreAdapter';

// --- 共通Props ---

type MemberSelectBaseProps = {
  /** 全要員リスト */
  members: MemberListResult[];
  /** ラベル */
  label?: string;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** 必須表示 */
  required?: boolean;
};

// --- 複数選択Props ---

type MemberSelectMultiProps = MemberSelectBaseProps & {
  /** 選択モード: 複数 */
  mode: 'multi';
  /** 選択中の要員IDリスト */
  selectedMemberIds: string[];
  /** 選択変更ハンドラ（複数） */
  onChange: (memberIds: string[]) => void;
};

// --- 単一選択Props ---

type MemberSelectSingleProps = MemberSelectBaseProps & {
  /** 選択モード: 単一 */
  mode: 'single';
  /** 選択中の要員ID（null=未選択） */
  selectedMemberId: string | null;
  /** 選択変更ハンドラ（単一） */
  onChange: (memberId: string | null) => void;
};

export type MemberSelectProps = MemberSelectMultiProps | MemberSelectSingleProps;

/**
 * 要員選択コンポーネント
 *
 * Autocomplete ベースで要員を検索・選択可能。
 * mode="multi" で複数選択、mode="single" で単一選択。
 * 要員名・カナで絞り込みができる。
 */
const MemberSelect: React.FC<MemberSelectProps> = (props) => {
  const {
    members,
    label = '要員',
    error = false,
    helperText,
    disabled = false,
    required = false,
  } = props;

  const filterOptions = (
    options: MemberListResult[],
    state: FilterOptionsState<MemberListResult>,
  ) => {
    const input = state.inputValue.toLowerCase();
    if (!input) return options;
    return options.filter(
      (m) =>
        m.memberName.toLowerCase().includes(input) ||
        m.memberNameKana.toLowerCase().includes(input),
    );
  };

  const renderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
    option: MemberListResult,
  ) => {
    const { key, ...rest } = optionProps;
    const subtitle = option.partnerName ? option.partnerName : '社員';
    return (
      <Box
        component="li"
        key={String(key)}
        {...rest}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}
      >
        <PersonOutlineIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {option.memberName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {subtitle}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (props.mode === 'single') {
    const selectedMember = members.find((m) => m.memberId === props.selectedMemberId) || null;

    return (
      <Autocomplete
        options={members}
        value={selectedMember}
        onChange={(_, newValue) => props.onChange(newValue?.memberId || null)}
        getOptionLabel={(option) => option.memberName}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option, value) => option.memberId === value.memberId}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            required={required}
            error={error}
            helperText={helperText}
            placeholder="要員を検索..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        )}
        renderOption={renderOption}
        noOptionsText="該当する要員がありません"
        sx={{ width: '100%' }}
      />
    );
  }

  // mode === 'multi'
  const selectedMembers = members.filter((m) => props.selectedMemberIds.includes(m.memberId));

  return (
    <Autocomplete
      multiple
      options={members}
      value={selectedMembers}
      onChange={(_, newValue) => props.onChange(newValue.map((m) => m.memberId))}
      getOptionLabel={(option) => option.memberName}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, value) => option.memberId === value.memberId}
      disabled={disabled}
      disableCloseOnSelect
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          required={required}
          error={error}
          helperText={helperText}
          placeholder={selectedMembers.length === 0 ? '要員を検索...' : ''}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      )}
      renderOption={renderOption}
      renderTags={(value, getTagProps) =>
        value.map((member, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...tagProps}
              label={member.memberName}
              size="small"
              icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{
                borderRadius: 1.5,
                fontWeight: 500,
                '& .MuiChip-icon': { color: 'primary.main' },
              }}
            />
          );
        })
      }
      noOptionsText="該当する要員がありません"
      sx={{ width: '100%' }}
    />
  );
};

export default MemberSelect;
